import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PackageOpen } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import { useStockSeat } from '../../hooks/useStockSeat';
import { formatLocalDateInTz, getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { errorMessage, unwrap } from '../../utils/apiResponse';
import { parseQuantity } from '../../utils/orderSheet';
import type { NurseryLoad, NurseryOrder, NurseryOrderLine, NurseryOrderStatus } from '../../types/nursery';

/**
 * /nursery/orders/:id/draw-down — take some of a bulk order now (Rob,
 * 2026-10-19; the legacy pots/order_drawdown.asp).
 *
 * Each line of the bulk order has a box for how many to take. The trees go
 * onto a new order of their own, confirmed, at the bulk order's prices and in
 * its place in the line for stock, with any trees already allocated to them;
 * the bulk order keeps the rest. The server does all of it in one step.
 */

const DRAWABLE: NurseryOrderStatus[] = ['confirmed', 'partially_allocated', 'allocated'];
/** Loads an order can still join (the server's ADDABLE_LOAD_STATUSES). */
const ADDABLE_LOADS = ['planning', 'released', 'picking', 'loaded'];

const inputCls =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function lineName(line: NurseryOrderLine): string {
  if (line.product_name) return line.product_name;
  return [line.cultivar_name, line.rootstock_name].filter(Boolean).join(' / ');
}

function OrderDrawDown() {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDrawDown' });
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: seatLoading } = useStockSeat('manage');

  const [order, setOrder] = useState<NurseryOrder | null>(null);
  const [loads, setLoads] = useState<NurseryLoad[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [take, setTake] = useState<Record<number, string>>({});
  const [customerPo, setCustomerPo] = useState('');
  const [wantedBy, setWantedBy] = useState(() => getTodayInTz(tz));
  const [loadId, setLoadId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [orderRes, loadRes] = await Promise.all([
        nurseryService.getOrder(orderId),
        nurseryService.getLoads({ open: true }).catch(() => null),
      ]);
      const o = unwrap(orderRes, t('loadFailed'));
      setOrder(o);
      setCustomerPo(o.customer_po ?? '');
      if (loadRes?.success) setLoads((loadRes.data || []).filter((l) => ADDABLE_LOADS.includes(l.status)));
    } catch (e) {
      setLoadError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [orderId, t]);

  useEffect(() => { load(); }, [load]);

  const lines = useMemo(() => (order?.lines || []).filter((l) => l.quantity_ordered > 0), [order]);

  /** What each box says, or null where it is not a whole number in range. */
  const parsed = useMemo(() => {
    const out: Record<number, number | null> = {};
    for (const l of lines) {
      const q = parseQuantity(take[l.id] ?? '');
      out[l.id] = q == null || q > l.quantity_ordered ? null : q;
    }
    return out;
  }, [lines, take]);

  const bad = lines.filter((l) => parsed[l.id] == null);
  const chosen = lines.filter((l) => (parsed[l.id] ?? 0) > 0);
  const trees = chosen.reduce((n, l) => n + (parsed[l.id] ?? 0), 0);
  const remaining = lines.reduce((n, l) => n + l.quantity_ordered, 0);

  const takeAll = () => setTake(Object.fromEntries(lines.map((l) => [l.id, String(l.quantity_ordered)])));
  const clearAll = () => setTake({});

  const submit = async () => {
    if (!order || !chosen.length || bad.length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = unwrap(await nurseryService.drawDownOrder(order.id, {
        lines: chosen.map((l) => ({ line_id: l.id, quantity: parsed[l.id] as number })),
        customer_po: customerPo.trim() || null,
        despatch_by: wantedBy || null,
        notes: notes.trim() || null,
        load_id: loadId,
      }), t('saveFailed'));
      navigate(`/nursery/orders/${result.order_id}`, {
        state: { drawnDown: { from: order.order_number, trees: result.trees, completed: result.source_completed } },
      });
    } catch (e) {
      setSaveError(errorMessage(e, t('saveFailed')));
      setSaving(false);
    }
  };

  const header = (
    <PageHeader
      title={order ? t('title', { number: order.order_number }) : t('titlePlain')}
      subtitle={order?.customer_name ? t('subtitle', { customer: order.customer_name }) : undefined}
      icon={<PackageOpen className="h-6 w-6 text-white" />}
    />
  );

  if (loading || seatLoading) {
    return (
      <>
        {header}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-neutral-100 rounded-lg" />
            <div className="h-64 bg-neutral-100 rounded-lg" />
          </div>
        </div>
      </>
    );
  }

  const back = (
    <Button variant="outline" onClick={() => navigate(`/nursery/orders/${orderId}`)}>
      <ArrowLeft className="h-4 w-4" /> {t('backToOrder')}
    </Button>
  );

  if (loadError || !order) {
    return (
      <>
        {header}
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words" role="alert">
            {loadError || t('loadFailed')}
          </div>
          {back}
        </div>
      </>
    );
  }

  if (!canManage || !DRAWABLE.includes(order.status) || !lines.length) {
    const reason = !canManage ? t('seatRequired')
      : order.status === 'draft' ? t('notConfirmed')
      : !lines.length ? t('nothingLeft')
      : t('cannotDraw', { status: t(`status.${order.status}`, { defaultValue: order.status.replace(/_/g, ' ') }) });
    return (
      <>
        {header}
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">{reason}</div>
          {back}
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-32 space-y-5">
        <p className="text-sm text-neutral-600 break-words">{t('intro')}</p>

        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-neutral-100">
            <h2 className="text-sm font-semibold text-neutral-700">
              {t('linesHeading', { count: remaining })}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={takeAll}>{t('takeAll')}</Button>
              {Object.keys(take).length > 0 && (
                <Button size="sm" variant="ghost" onClick={clearAll}>{t('clear')}</Button>
              )}
            </div>
          </div>
          <ul className="divide-y divide-neutral-100">
            {lines.map((l) => {
              const value = take[l.id] ?? '';
              const invalid = value.trim() !== '' && parsed[l.id] == null;
              const inputId = `draw-${l.id}`;
              return (
                <li key={l.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <label htmlFor={inputId} className="block text-sm font-medium text-neutral-900 break-words">
                      {lineName(l)}
                    </label>
                    <p className="text-xs text-neutral-500 break-words">
                      {l.tree_type_name && <span>{l.tree_type_name} · </span>}
                      {t('onOrder', { count: l.quantity_ordered })}
                      {l.quantity_allocated > 0 && <span> · {t('allocated', { count: l.quantity_allocated })}</span>}
                    </p>
                    {invalid && (
                      <p className="text-xs text-red-600 mt-0.5" role="alert">{t('tooMany', { count: l.quantity_ordered })}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      id={inputId}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="0"
                      value={value}
                      onChange={(e) => setTake((prev) => ({ ...prev, [l.id]: e.target.value }))}
                      aria-invalid={invalid}
                      className={`w-24 border rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500 ${invalid ? 'border-red-400' : 'border-neutral-300'}`}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setTake((prev) => ({ ...prev, [l.id]: String(l.quantity_ordered) }))}
                    >
                      {t('all')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4">
          <h2 className="text-sm font-semibold text-neutral-700">{t('newOrderHeading')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <label htmlFor="draw-po" className="block text-sm font-medium text-neutral-700 mb-1">{t('customerPo')}</label>
              <input
                id="draw-po"
                type="text"
                maxLength={100}
                value={customerPo}
                onChange={(e) => setCustomerPo(e.target.value)}
                className={inputCls}
              />
              <p className="text-xs text-neutral-500 mt-1 break-words">{t('poHint')}</p>
            </div>
            <div className="min-w-0">
              <label htmlFor="draw-wanted-by" className="block text-sm font-medium text-neutral-700 mb-1">{t('wantedBy')}</label>
              <input
                id="draw-wanted-by"
                type="date"
                value={wantedBy}
                onChange={(e) => setWantedBy(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div className="min-w-0">
            <label htmlFor="draw-load" className="block text-sm font-medium text-neutral-700 mb-1">{t('load')}</label>
            <select
              id="draw-load"
              value={loadId ?? ''}
              onChange={(e) => setLoadId(e.target.value ? Number(e.target.value) : null)}
              className={inputCls}
            >
              <option value="">{t('noLoad')}</option>
              {loads.map((l) => (
                <option key={l.id} value={l.id}>
                  {t('loadOption', {
                    number: l.load_number,
                    date: formatLocalDateInTz(l.despatch_date, tz),
                    farm: l.despatch_farm_name,
                  })}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1 break-words">{t('loadHint')}</p>
          </div>
          <div className="min-w-0">
            <label htmlFor="draw-notes" className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
            <textarea
              id="draw-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
            />
          </div>
        </section>

        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words" role="alert">{saveError}</div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <Link to={`/nursery/orders/${order.id}`} className="text-sm text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> {t('backToOrder')}
          </Link>
          <div className="flex flex-col sm:items-end gap-1 min-w-0">
            <p className="text-sm text-neutral-600 break-words">
              {trees > 0
                ? (trees === remaining ? t('summaryAll', { count: trees }) : t('summary', { count: trees, left: remaining - trees }))
                : t('summaryNone')}
            </p>
            <Button onClick={submit} disabled={saving || !chosen.length || bad.length > 0} isLoading={saving}>
              {t('submit', { count: trees })}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderDrawDown;
